import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import './SpecificationsSection.css';

const SpecificationsSection = ({
    onOpenSpecificationGenerator,
    onExportSpecification,
    onDeleteSpecification,
    onClearAllSpecifications,
    onCreateNodesFromSpecification,
    refreshSpecifications: externalRefresh, // Fonction de refresh externe
    loading: propsLoading
}) => {
    const { t } = useTranslation();
    const [generatedSpecifications, setGeneratedSpecifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to force refresh
    const [editingSpec, setEditingSpec] = useState(null); // Specification being edited
    const [showEditModal, setShowEditModal] = useState(false); // Edit modal
    const [expandedSections, setExpandedSections] = useState({ basics: true }); // Collapsible sections
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    const refreshSpecifications = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    useEffect(() => {
        const fetchSpecifications = async () => {
            setLoading(true);
            try {
                // 1. Try to retrieve from API
                const response = await axios.get(`${API_BASE_URL}/api/specifications/list`, {
                    withCredentials: true
                });
                const specs = response.data.specifications || [];
                setGeneratedSpecifications(specs);
                console.log('✅ Spécifications récupérées depuis l\'API:', specs.length);
                
            } catch (error) {
                console.error('❌ Erreur API, fallback vers localStorage:', error);
                
                // 2. Fallback to localStorage if API fails
                try {
                    const savedSpecifications = localStorage.getItem("generatedSpecifications");
                    if (savedSpecifications) {
                        const parsedSpecs = JSON.parse(savedSpecifications);
                        if (Array.isArray(parsedSpecs)) {
                            // Format to be compatible with API
                            const formattedSpecs = parsedSpecs.map(spec => ({
                                id: spec.id || Date.now() + Math.random(),
                                createdAt: spec.createdAt || new Date().toISOString(),
                                content: {
                                    specification: spec.specification || spec.content?.specification,
                                    priority: spec.priority || 'medium'
                                },
                                title: spec.specification?.title || spec.content?.specification?.title,
                                description: spec.specification?.summary || spec.content?.specification?.summary
                            }));
                            setGeneratedSpecifications(formattedSpecs);
                            console.log('📱 Spécifications chargées depuis localStorage:', formattedSpecs.length);
                        }
                    }
                } catch (localError) {
                    console.error('❌ Erreur localStorage:', localError);
                    setGeneratedSpecifications([]);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSpecifications();
    }, [API_BASE_URL, refreshTrigger]); // Add refreshTrigger to dependencies

    // Exposer la fonction de refresh si fournie par le parent
    useEffect(() => {
        if (externalRefresh && externalRefresh.current !== undefined) {
            // Passer notre fonction de refresh au parent
            externalRefresh.current = refreshSpecifications;
        }
    }, [externalRefresh]);

    const handleOpenSpecificationGenerator = onOpenSpecificationGenerator;

    // Helper pour parser et formater les données JSON pour l'affichage HTML
    const parseAndFormatJsonContent = (content, sectionType) => {
        if (!content) return 'Non défini';
        
        try {
            // Essayer de parser le JSON si c'est une chaîne
            let parsedContent = content;
            if (typeof content === 'string') {
                const trimmed = content.trim();
                
                console.log('🔍 parseAndFormatJsonContent processing:', { 
                    content: trimmed.substring(0, 200), 
                    sectionType 
                });
                
                // Cas 1: Format numéroté (1. {JSON}, 2. {JSON}, etc.)
                const numberedItems = [];
                const numberedPattern = /(\d+)\.\s*(\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\})/g;
                let match;
                while ((match = numberedPattern.exec(trimmed)) !== null) {
                    try {
                        const jsonObj = JSON.parse(match[2]);
                        numberedItems.push({ number: match[1], data: jsonObj });
                    } catch (e) {
                        console.log('❌ Failed to parse numbered JSON:', match[2]);
                    }
                }
                
                if (numberedItems.length > 0) {
                    console.log('✅ Found numbered items:', numberedItems.length);
                    parsedContent = numberedItems;
                } 
                // Cas 2: Multiples objets JSON séparés par des lignes
                else if (trimmed.includes('}\n\n{') || trimmed.includes('}\n{') || (trimmed.match(/\{[\s\S]*?\}/g) && trimmed.match(/\{[\s\S]*?\}/g).length > 1)) {
                    console.log('🔍 Trying to split multiple JSON objects...');
                    const jsonObjects = [];
                    
                    // Méthode 1: Regex pour extraire tous les objets JSON
                    const jsonMatches = trimmed.match(/\{[\s\S]*?\}(?=\s*(?:\{|$))/g);
                    if (jsonMatches && jsonMatches.length > 1) {
                        for (const match of jsonMatches) {
                            try {
                                const jsonObj = JSON.parse(match.trim());
                                jsonObjects.push(jsonObj);
                            } catch (e) {
                                console.log('❌ Failed to parse regex JSON:', match.substring(0, 100));
                            }
                        }
                    } else {
                        // Méthode 2: Split par patterns
                        const parts = trimmed.split(/\}\s*\n\s*\{/);
                        
                        for (let i = 0; i < parts.length; i++) {
                            let part = parts[i].trim();
                            // Ajouter les accolades manquantes
                            if (i > 0) part = '{' + part;
                            if (i < parts.length - 1) part = part + '}';
                            
                            try {
                                const jsonObj = JSON.parse(part);
                                jsonObjects.push(jsonObj);
                            } catch (e) {
                                console.log('❌ Failed to parse split JSON:', part.substring(0, 100));
                            }
                        }
                    }
                    
                    if (jsonObjects.length > 0) {
                        console.log('✅ Found split JSON objects:', jsonObjects.length);
                        parsedContent = jsonObjects;
                    }
                }
                // Cas 3: Un seul objet JSON
                else if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                    try {
                        parsedContent = JSON.parse(trimmed);
                        console.log('✅ Parsed single JSON object');
                    } catch (e) {
                        console.log('❌ Failed to parse single JSON:', trimmed.substring(0, 100));
                    }
                }
            }
            
            // Formater selon le type de section
            const result = formatContentByType(parsedContent, sectionType);
            console.log('✅ parseAndFormatJsonContent result length:', result.length);
            return result;
            
        } catch (error) {
            console.error('❌ Error in parseAndFormatJsonContent:', error);
            return content; // Retourner le contenu original en cas d'erreur
        }
    };

    // Formater le contenu selon le type
    const formatContentByType = (content, sectionType) => {
        console.log('🔍 formatContentByType called:', { 
            content: typeof content === 'object' ? 'object' : content?.substring?.(0, 100), 
            sectionType, 
            isArray: Array.isArray(content),
            contentType: typeof content
        });
        
        if (Array.isArray(content) && content.length > 0 && content[0].data) {
            // Format numéroté avec objets JSON
            console.log('✅ Processing numbered items:', content.length);
            return content.map(item => formatSingleItem(item.data, sectionType, item.number)).join('\n\n');
        } else if (Array.isArray(content)) {
            console.log('✅ Processing array items:', content.length);
            return content.map((item, index) => formatSingleItem(item, sectionType, index + 1)).join('\n\n');
        } else if (typeof content === 'object' && content !== null) {
            console.log('✅ Processing single object');
            return formatSingleItem(content, sectionType);
        } else {
            console.log('✅ Processing as string');
            return String(content);
        }
    };

    // Simplified legacy formatter (kept only as fallback; primary path uses SECTION_RENDERERS)
    const formatSingleItem = (item, sectionType, number = null) => {
        if (!item || typeof item !== 'object') return String(item);
        const numberPrefix = number ? `${number}. ` : '';
        return `${numberPrefix}<div class="formatted-item json-fallback"><pre>${JSON.stringify(item,null,2)}</pre></div>`;
    };

    const handleViewSpecification = (spec) => {
        // Utility function to process data safely with improved formatting
        const safeDisplay = (value, sectionType = 'generic') => {
            if (!value) return t('common.notDefined');

            // Normalize sectionType mapping
            const normalized = ({
              userStories:'stories', stories:'stories',
              testCases:'tests', tests:'tests',
              acceptanceCriteria:'acceptance', acceptance:'acceptance',
              functionalRequirements:'requirements', nonFunctionalRequirements:'requirements', requirements:'requirements', nfrequirements:'requirements'
            })[sectionType] || sectionType;

            try {
                            // Step 1: attempt to parse raw string into JS data (NOT HTML)
                            let processed = value;
                            if (typeof value === 'string') {
                                // parseAndFormatJsonContent currently returns HTML; we only want data. So attempt manual JSON parse heuristics first.
                                const trimmed = value.trim();
                                if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                                    try { processed = JSON.parse(trimmed); } catch(_e) { /* ignore */ }
                                } else if (/\d+\.\s*\{/.test(trimmed)) {
                                    // Numbered JSON blocks
                                    const blocks = [];
                                    const re = /(\d+)\.\s*(\{[\s\S]*?\})(?=\n\d+\.|$)/g;
                                    let m; while((m=re.exec(trimmed))){ try { blocks.push(JSON.parse(m[2])); } catch(_) {} }
                                    if (blocks.length) processed = blocks;
                                }
                            }

              // Step 2: unified renderer if available and data is object/array
              if (SECTION_RENDERERS[normalized] && (Array.isArray(processed) || typeof processed==='object' || typeof processed==='string')) {
                return SECTION_RENDERERS[normalized](processed);
              }

              // Step 2.5: Fallback to generic renderer for unknown section types
              if ((Array.isArray(processed) || typeof processed==='object' || typeof processed==='string') && processed) {
                return SECTION_RENDERERS.generic(processed);
              }                            // Step 3: If value was string and SECTION_RENDERERS not applied, fallback to previous parsing -> may produce HTML already
                            if (typeof value === 'string') {
                                const htmlAttempt = parseAndFormatJsonContent(value, normalized);
                                if (typeof htmlAttempt === 'string' && htmlAttempt.includes('<div')) return htmlAttempt;
                                processed = htmlAttempt;
                            }

                            // Step 4: Generic object/array fallback
                            if (Array.isArray(processed) || (processed && typeof processed==='object')) {
                                return formatContentByType(processed, normalized);
                            }
                            return String(processed ?? '');
            } catch (e) {
              console.error('[safeDisplay] render error', e);
              return `<div class="json-fallback"><pre>${typeof value === 'string'?value:JSON.stringify(value,null,2)}</pre></div>`;
            }
        };

        // Extract data safely
        const specData = spec.content?.specification || {};
        const title = specData.title || spec.title || t('specificationsSection.titleFallback');

        // Use same section logic as in SpecificationGenerator.js
        const sections = [
            { id: 'summary', label: '📋 Résumé', show: specData.summary, content: safeDisplay(specData.summary, 'summary') },
            { id: 'functional', label: '📝 Description', show: specData.functionalDescription, content: safeDisplay(specData.functionalDescription, 'functional') },
            { id: 'business', label: '💼 Business', show: specData.businessValue, content: safeDisplay(specData.businessValue, 'business') },
            { id: 'personas', label: '👥 Personas', show: specData.personas?.length > 0, content: safeDisplay(specData.personas, 'personas') },
            { id: 'usecases', label: '🎯 Use Cases', show: specData.useCases?.length > 0, content: safeDisplay(specData.useCases, 'usecases') },
            { id: 'stories', label: '📖 User Stories', show: specData.userStories?.length > 0, content: safeDisplay(specData.userStories, 'stories') },
            { id: 'requirements', label: '⚙️ Exigences Fonct.', show: specData.functionalRequirements?.length > 0, content: safeDisplay(specData.functionalRequirements, 'requirements') },
            { id: 'nfrequirements', label: '🔧 Exigences Non-Fonct.', show: specData.nonFunctionalRequirements?.length > 0, content: safeDisplay(specData.nonFunctionalRequirements, 'nfrequirements') },
            { id: 'acceptance', label: '✅ Acceptation', show: specData.acceptanceCriteria?.length > 0, content: safeDisplay(specData.acceptanceCriteria, 'acceptance') },
            { id: 'tests', label: '🧪 Tests', show: specData.testCases?.length > 0 || specData.mainTestCases?.length > 0, content: safeDisplay(specData.testCases || specData.mainTestCases, 'tests') },
            { id: 'risks', label: '⚠️ Risques', show: specData.risks?.length > 0, content: safeDisplay(specData.risks, 'risks') },
            { id: 'dependencies', label: '🔗 Dépendances', show: specData.dependencies?.length > 0, content: safeDisplay(specData.dependencies, 'dependencies') },
            { id: 'impacts', label: '💥 Impacts', show: specData.impacts?.length > 0, content: safeDisplay(specData.impacts, 'impacts') },
            { id: 'dod', label: '✔️ Definition of Done', show: specData.definitionOfDone?.length > 0, content: safeDisplay(specData.definitionOfDone, 'dod') },
            { id: 'architecture', label: '🏗️ Architecture', show: specData.architecture || specData.technicalDetails || specData.systemArchitecture, content: safeDisplay(specData.architecture || specData.technicalDetails || specData.systemArchitecture, 'architecture') },
            { id: 'wireframes', label: '🎨 Wireframes', show: specData.wireframes?.length > 0 || specData.mockups?.length > 0, content: safeDisplay(specData.wireframes || specData.mockups) },
            { id: 'datamodel', label: '📊 Modèle de Données', show: specData.dataModel || specData.entities || specData.databaseDesign, content: safeDisplay(specData.dataModel || specData.entities || specData.databaseDesign) },
            { id: 'security', label: '🔒 Sécurité', show: specData.security || specData.securityRequirements?.length > 0, content: safeDisplay(specData.security || specData.securityRequirements) },
            { id: 'performance', label: '⚡ Performance', show: specData.performance || specData.performanceRequirements?.length > 0, content: safeDisplay(specData.performance || specData.performanceRequirements) },
            { id: 'api', label: '🔌 API', show: specData.api || specData.endpoints?.length > 0 || specData.apiSpecification, content: safeDisplay(specData.api || specData.endpoints || specData.apiSpecification) },
            { id: 'constraints', label: '⚠️ Contraintes', show: specData.constraints || specData.technicalConstraints?.length > 0, content: safeDisplay(specData.constraints || specData.technicalConstraints) },
            { id: 'timeline', label: '📅 Planning', show: specData.timeline || specData.milestones?.length > 0 || specData.deadlines, content: safeDisplay(specData.timeline || specData.milestones || specData.deadlines) },
            { id: 'stakeholders', label: '👤 Parties Prenantes', show: specData.stakeholders?.length > 0, content: safeDisplay(specData.stakeholders) },
            { id: 'glossary', label: '📖 Glossaire', show: specData.glossary || specData.definitions?.length > 0, content: safeDisplay(specData.glossary || specData.definitions) },
            { id: 'annexes', label: '📎 Annexes', show: specData.annexes || specData.attachments?.length > 0 || specData.references?.length > 0, content: safeDisplay(specData.annexes || specData.attachments || specData.references) }
        ];

        // Filtrer seulement les sections qui ont du contenu
        const visibleSections = sections.filter(section => section.show);

        // Estimates with safe handling
        const estimations = specData.estimations || {};
        const complexity = estimations.complexity || t('common.notDefined');
        const effort = estimations.effort || t('common.notDefined');
        const storyPoints = estimations.storyPoints || t('common.notDefined');

        // Create a new window to display details
        const newWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
        if (newWindow) {
            newWindow.document.write(`
        <html>
          <head>
            <title>Spécification - ${title}</title>
            <style>
              body {
                font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Inter', sans-serif;
                max-width: 920px;
                margin: 0 auto;
                padding: 24px;
                line-height: 1.65;
                background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                color: #1e293b;
                min-height: 100vh;
              }
              .spec-container {
                background: #ffffff;
                padding: 40px;
                border-radius: 20px;
                box-shadow: 
                  0 20px 25px -5px rgba(0, 0, 0, 0.1),
                  0 10px 10px -5px rgba(0, 0, 0, 0.04),
                  0 0 0 1px rgba(148, 163, 184, 0.1);
                margin-bottom: 32px;
                backdrop-filter: blur(20px);
                border: 1px solid rgba(226, 232, 240, 0.8);
                position: relative;
                overflow: hidden;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              }
              .spec-container::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 4px;
                background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4);
                border-radius: 20px 20px 0 0;
              }
              .spec-container:hover {
                transform: translateY(-2px);
                box-shadow: 
                  0 25px 50px -12px rgba(0, 0, 0, 0.15),
                  0 0 0 1px rgba(59, 130, 246, 0.1);
              }
              h1 { 
                color: #0f172a; 
                border-bottom: none;
                padding: 0 0 20px 0; 
                margin: 0 0 32px 0;
                font-size: 32px;
                font-weight: 800;
                background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
                position: relative;
              }
              h1::after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: 60px;
                height: 4px;
                background: linear-gradient(90deg, #3b82f6, #8b5cf6);
                border-radius: 2px;
              }
              h2 { 
                color: #1e293b; 
                margin: 32px 0 20px 0; 
                font-size: 22px;
                font-weight: 700;
                border: none;
                padding: 16px 20px;
                background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                border-radius: 12px;
                border-left: 4px solid #3b82f6;
                position: relative;
                transition: all 0.2s ease;
              }
              h2:hover {
                background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
                transform: translateX(4px);
              }
              h2::before {
                content: '▶';
                position: absolute;
                left: -2px;
                top: 50%;
                transform: translateY(-50%);
                color: #3b82f6;
                font-size: 12px;
                opacity: 0.6;
              }
              .section { 
                margin-bottom: 28px; 
                padding: 24px;
                background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                border-radius: 16px;
                border: 1px solid rgba(226, 232, 240, 0.8);
                box-shadow: 
                  0 4px 6px -1px rgba(0, 0, 0, 0.05),
                  0 2px 4px -1px rgba(0, 0, 0, 0.03);
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;
              }
              .section::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background: linear-gradient(180deg, #3b82f6, #8b5cf6);
                opacity: 0.8;
              }
              .section:hover {
                transform: translateY(-1px);
                box-shadow: 
                  0 10px 15px -3px rgba(0, 0, 0, 0.08),
                  0 4px 6px -2px rgba(0, 0, 0, 0.04);
              }
              .meta { 
                color: #64748b; 
                font-style: italic; 
                background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                padding: 20px;
                border-radius: 12px;
                margin-top: 32px;
                border: 1px solid rgba(148, 163, 184, 0.2);
                font-size: 14px;
                backdrop-filter: blur(10px);
              }
              .content {
                white-space: pre-wrap;
                font-family: 'JetBrains Mono', 'Fira Code', 'Monaco', 'Consolas', monospace;
                background: #fefefe;
                padding: 20px;
                border-radius: 12px;
                border: 1px solid rgba(226, 232, 240, 0.6);
                margin-top: 16px;
                font-size: 14px;
                line-height: 1.7;
                box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.02);
                transition: all 0.2s ease;
              }
              .content:hover {
                border-color: rgba(59, 130, 246, 0.3);
                box-shadow: 
                  inset 0 2px 4px 0 rgba(0, 0, 0, 0.02),
                  0 0 0 3px rgba(59, 130, 246, 0.1);
              }
              .print-btn {
                position: fixed;
                top: 24px;
                right: 24px;
                background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                color: white;
                border: none;
                padding: 16px 28px;
                border-radius: 12px;
                cursor: pointer;
                font-weight: 600;
                font-size: 14px;
                z-index: 1000;
                box-shadow: 
                  0 10px 15px -3px rgba(59, 130, 246, 0.3),
                  0 4px 6px -2px rgba(59, 130, 246, 0.1);
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                backdrop-filter: blur(20px);
                border: 1px solid rgba(255, 255, 255, 0.1);
              }
              .print-btn:hover { 
                background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
                transform: translateY(-2px);
                box-shadow: 
                  0 15px 25px -5px rgba(59, 130, 246, 0.4),
                  0 10px 10px -5px rgba(59, 130, 246, 0.2);
              }
              .print-btn:active {
                transform: translateY(0);
              }
              .close-btn {
                position: fixed;
                top: 24px;
                right: 180px;
                background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                color: white;
                border: none;
                padding: 16px 28px;
                border-radius: 12px;
                cursor: pointer;
                font-weight: 600;
                font-size: 14px;
                z-index: 1000;
                box-shadow: 
                  0 10px 15px -3px rgba(239, 68, 68, 0.3),
                  0 4px 6px -2px rgba(239, 68, 68, 0.1);
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                backdrop-filter: blur(20px);
                border: 1px solid rgba(255, 255, 255, 0.1);
              }
              .close-btn:hover { 
                background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
                transform: translateY(-2px);
                box-shadow: 
                  0 15px 25px -5px rgba(239, 68, 68, 0.4),
                  0 10px 10px -5px rgba(239, 68, 68, 0.2);
              }
              .close-btn:active {
                transform: translateY(0);
              }
              @media print {
                .print-btn, .close-btn { display: none; }
                body { 
                  background: white; 
                  padding: 20px;
                }
                .spec-container { 
                  box-shadow: none; 
                  border: 1px solid #e5e7eb;
                  border-radius: 8px;
                }
                .spec-container::before { display: none; }
                .section { 
                  box-shadow: none;
                  border: 1px solid #e5e7eb;
                  break-inside: avoid;
                }
                .section::before { display: none; }
                h1 { 
                  color: #1e293b !important;
                  -webkit-text-fill-color: #1e293b !important;
                }
                h2 { 
                  background: #f8fafc !important;
                  color: #1e293b !important;
                }
              }
              
              /* Animations et micro-interactions */
              @keyframes fadeInUp {
                from {
                  opacity: 0;
                  transform: translateY(20px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
              
              .spec-container {
                animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
              }
              
              .section {
                animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                animation-fill-mode: both;
              }
              
              .section:nth-child(1) { animation-delay: 0.1s; }
              .section:nth-child(2) { animation-delay: 0.2s; }
              .section:nth-child(3) { animation-delay: 0.3s; }
              .section:nth-child(4) { animation-delay: 0.4s; }
              .section:nth-child(5) { animation-delay: 0.5s; }
              
              /* Styles pour les éléments formatés */
              .formatted-item {
                background: #f8fafc;
                border-radius: 12px;
                padding: 16px;
                margin: 12px 0;
                border-left: 4px solid #e2e8f0;
                transition: all 0.2s ease;
              }
              
              .formatted-item:hover {
                background: #f1f5f9;
                transform: translateX(2px);
              }
              
              .formatted-item h4 {
                margin: 0 0 12px 0;
                color: #1e293b;
                font-size: 16px;
                font-weight: 600;
              }
              
              .item-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-bottom: 12px;
                flex-wrap: wrap;
              }
              
              .scenario-id, .test-id, .risk-id, .story-id, .req-id {
                font-family: 'JetBrains Mono', monospace;
                font-size: 12px;
                font-weight: 600;
                padding: 3px 8px;
                border-radius: 6px;
                color: white;
              }
              
              .scenario-id { background: #22c55e; }
              .test-id { background: #f59e0b; }
              .risk-id { background: #ef4444; }
              .story-id { background: #3b82f6; }
              .req-id { background: #8b5cf6; }
              
              .priority, .test-type, .risk-category, .req-category {
                font-size: 11px;
                font-weight: 600;
                padding: 2px 6px;
                border-radius: 8px;
                text-transform: uppercase;
              }
              
              .priority-high { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
              .priority-medium { background: #fffbeb; color: #d97706; border: 1px solid #fed7aa; }
              .priority-low { background: #f0f9ff; color: #0284c7; border: 1px solid #bae6fd; }
              
              .test-type { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
              .risk-category { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
              .req-category { background: #f3e8ff; color: #7c3aed; border: 1px solid #d8b4fe; }
              
              .risk-badge {
                font-size: 10px;
                font-weight: 600;
                padding: 2px 6px;
                border-radius: 8px;
                color: white;
                text-transform: uppercase;
              }
              
              .probability-high, .impact-high { background: #dc2626; }
              .probability-medium, .impact-medium { background: #f59e0b; }
              .probability-low, .impact-low { background: #10b981; }
              
              .gherkin-steps, .test-section {
                background: rgba(255, 255, 255, 0.7);
                border-radius: 8px;
                padding: 12px;
                margin: 8px 0;
              }
              
              .gherkin-section {
                margin-bottom: 12px;
              }
              
              .gherkin-section strong {
                color: #059669;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              
              .gherkin-section ul, .test-section ul {
                margin: 8px 0 0 16px;
                padding: 0;
              }
              
              .gherkin-section li, .test-section li {
                color: #374151;
                margin-bottom: 4px;
                line-height: 1.4;
              }
              
              .test-section ol {
                margin: 8px 0 0 16px;
                padding: 0;
              }
              
              .test-result, .mitigation {
                background: #f0f9ff;
                border: 1px solid #0ea5e9;
                border-radius: 8px;
                padding: 12px;
                margin-top: 12px;
                color: #0369a1;
              }
              
              .mitigation {
                background: #f0fdf4;
                border-color: #10b981;
                color: #065f46;
              }
              
              .story-text, .description {
                color: #6b7280;
                font-style: italic;
                margin: 8px 0;
                line-height: 1.5;
              }
              
              .acceptance-criteria {
                background: #f0fdf4;
                border-radius: 8px;
                padding: 12px;
                margin-top: 12px;
                border-left: 3px solid #22c55e;
              }
              
              .acceptance-criteria strong {
                color: #059669;
              }
              
              .criteria, .impacted-systems {
                margin-top: 8px;
                padding: 8px;
                background: rgba(255, 255, 255, 0.5);
                border-radius: 6px;
              }
              
              .field {
                margin: 4px 0;
                color: #4b5563;
              }
              
              .field strong {
                color: #374151;
              }
              
              .json-fallback {
                background: #1f2937;
                color: #f9fafb;
                font-family: 'JetBrains Mono', monospace;
                font-size: 12px;
                overflow-x: auto;
              }
              
              .json-fallback pre {
                margin: 0;
                white-space: pre-wrap;
              }

              /* === Unified structured cards === */
              .structured-card {background:#ffffffeb;border:1px solid #e2e8f0;border-radius:14px;padding:14px 16px;margin:14px 0;box-shadow:0 2px 4px -2px rgba(0,0,0,0.06);position:relative;overflow:hidden;}
              .structured-card:before{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(120deg,rgba(59,130,246,0.08),rgba(16,185,129,0.05));opacity:0;transition:opacity .25s;}
              .structured-card:hover:before{opacity:1;}
              .structured-card h4{margin:4px 0 10px;font-size:15px;font-weight:600;color:#0f172a;}
              .structured-card .card-head{display:flex;gap:6px;align-items:center;flex-wrap:wrap;margin-bottom:6px;}
              .structured-card .badge{font-size:11px;font-weight:600;padding:3px 7px;border-radius:6px;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;letter-spacing:.3px;text-transform:uppercase;}
              .structured-card .badge.id{background:#3b82f6;color:#fff;border:none;}
              .structured-card .badge.type{background:#6366f1;color:#fff;border:none;}
              .structured-card .badge.prio-high{background:#dc2626;color:#fff;border:none;}
              .structured-card .badge.prio-medium{background:#f59e0b;color:#fff;border:none;}
              .structured-card .badge.prio-low{background:#0ea5e9;color:#fff;border:none;}
              .structured-card .badge.cat{background:#6b7280;color:#fff;border:none;}
              .structured-card .story{color:#475569;font-style:italic;margin:6px 0 4px;}
              .structured-card .desc{color:#475569;margin:4px 0 10px;}
              .structured-card .mini-section{background:#f8fafc;border:1px solid #e2e8f0;padding:8px 10px;border-radius:8px;margin-top:10px;font-size:13px;}
              .structured-card .mini-section ul, .structured-card .mini-section ol{margin:6px 0 0 18px;padding:0;}
              .structured-card .mini-section li{margin:2px 0;}
              .structured-card .result{margin-top:10px;background:#eef9ff;border:1px solid #bae6fd;padding:8px 10px;border-radius:8px;font-size:13px;color:#0369a1;}
              .ac-card .gherkin{display:flex;flex-wrap:wrap;gap:12px;margin-top:8px;}
              .ac-card .gh-block{flex:1 1 140px;min-width:140px;background:#f8fafc;border:1px solid #e2e8f0;padding:8px 10px;border-radius:8px;}
              .ac-card .gh-block span{display:inline-block;font-size:11px;font-weight:700;color:#059669;text-transform:uppercase;margin-bottom:4px;letter-spacing:.5px;}
              .risk-card .risk-meta{display:flex;gap:6px;margin:4px 0 6px;}
              .risk-card .badge.prob-low{background:#10b981;color:#fff;}
              .risk-card .badge.prob-medium{background:#f59e0b;color:#fff;}
              .risk-card .badge.prob-high{background:#dc2626;color:#fff;}
              .risk-card .badge.impact-low{background:#10b981;color:#fff;}
              .risk-card .badge.impact-medium{background:#f59e0b;color:#fff;}
              .risk-card .badge.impact-high{background:#dc2626;color:#fff;}
              .mitigation{margin-top:8px;}
              .business-block{display:flex;flex-wrap:wrap;gap:18px;}
              .business-block .sub{flex:1 1 180px;min-width:180px;background:#f8fafc;border:1px solid #e2e8f0;padding:10px 12px;border-radius:10px;}
              .business-block h5{margin:0 0 6px;font-size:12px;font-weight:700;letter-spacing:.5px;color:#334155;text-transform:uppercase;}
              .business-block ul{margin:0;padding-left:18px;}
              .business-block li{margin:2px 0;font-size:13px;}
              .structured-card .content{color:#475569;margin:4px 0 10px;line-height:1.5;}
              .structured-card .mitigation{margin-top:10px;background:#f0fdf4;border:1px solid #bbf7d0;padding:8px 10px;border-radius:8px;font-size:13px;color:#166534;}
              .empty-section{text-align:center;color:#94a3b8;font-style:italic;padding:20px;background:#f8fafc;border:1px dashed #cbd5e1;border-radius:8px;margin:10px 0;}

            </style>
          </head>
          <body>
            <button class="close-btn" id="close-btn">❌ Fermer</button>
            <button class="print-btn" id="print-btn">🖨️ Imprimer</button>
            
            <script>
              document.getElementById('close-btn').addEventListener('click', function() {
                window.close();
              });
              
              document.getElementById('print-btn').addEventListener('click', function() {
                window.print();
              });
            </script>
            
            <div class="spec-container">
              <h1>📋 ${title}</h1>
              
              ${visibleSections.map(section => `
                <div class="section">
                  <h2>${section.label}</h2>
                  <div class="content">${section.content}</div>
                </div>
              `).join('')}
              
              ${(estimations.complexity !== 'Non définie' || estimations.effort !== 'Non défini' || estimations.storyPoints !== 'Non définis') ? `
              <div class="section">
                <h2>📊 Estimations</h2>
                <div class="content">Complexité: ${complexity}
Effort: ${effort}
Story Points: ${storyPoints}</div>
              </div>
              ` : ''}

              <div class="meta">
                <strong>📅 Généré le:</strong> ${new Date(spec.createdAt).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })}
              </div>
            </div>
          </body>
        </html>
      `);
            newWindow.document.close();
        } else {
            // Fallback if popup is blocked - create readable plain text
            const fallbackText = `
🎯 SPÉCIFICATION: ${title}

${visibleSections.map(section => `
${section.label}:
${section.content}
`).join('\n')}

${(estimations.complexity !== 'Non définie' || estimations.effort !== 'Non défini' || estimations.storyPoints !== 'Non définis') ? `
📊 ESTIMATIONS:
- Complexité: ${complexity}
- Effort: ${effort}
- Story Points: ${storyPoints}
` : ''}

---
📅 Généré le: ${new Date(spec.createdAt).toLocaleDateString('fr-FR')}
      `.trim();

            toast.info(fallbackText);
        }
    };

    const handleExportSpecification = onExportSpecification || ((spec) => {
        // Use same structure as generation
        const exportData = {
            specification: spec.content?.specification || spec.specification,
            id: spec.id,
            createdAt: spec.createdAt,
            priority: spec.content?.priority || spec.priority
        };
        
        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `specification-${exportData.specification?.title?.replace(/[^a-z0-9]/gi, '-') || 'unnamed'}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    });

    const handleDeleteSpecification = async (specId) => {
        if (!window.confirm(t('specificationsSection.confirmDeleteSpec'))) {
            return;
        }

        try {
            // 1. Essayer de supprimer via API
            await axios.delete(`${API_BASE_URL}/api/specifications/${specId}`, {
                withCredentials: true
            });
            console.log('✅ Spécification supprimée via API');
            
        } catch (error) {
            console.error('❌ Erreur API lors de la suppression, fallback localStorage:', error);
        }

        // 2. Supprimer localement dans tous les cas
        setGeneratedSpecifications(prev => {
            const updatedSpecs = prev.filter(spec => spec.id !== specId);
            
            // Sauvegarder dans localStorage
            try {
                localStorage.setItem("generatedSpecifications", JSON.stringify(updatedSpecs));
            } catch (e) {
                console.error('❌ Erreur sauvegarde localStorage:', e);
            }
            
            return updatedSpecs;
        });

        if (onDeleteSpecification) {
            onDeleteSpecification(specId);
        }
    };

    const handleClearAllSpecifications = async () => {
        if (!window.confirm(t('specificationsSection.confirmDeleteAllSpecs'))) {
            return;
        }

        try {
            // 1. Essayer de supprimer via API
            await axios.delete(`${API_BASE_URL}/api/specifications/clear-all`, {
                withCredentials: true
            });
            console.log('✅ Toutes les spécifications supprimées via API');
            
        } catch (error) {
            console.error('❌ Erreur API lors de la suppression globale:', error);
        }

        // 2. Nettoyer localement dans tous les cas
        setGeneratedSpecifications([]);
        localStorage.removeItem("generatedSpecifications");
        
        if (onClearAllSpecifications) {
            onClearAllSpecifications();
        }
    };

    const handleCopySpecification = (spec) => {
        const specText = `# ${spec.specification?.title || 'Spécification'}

## Description
${spec.specification?.functionalDescription || spec.specification?.summary || 'Aucune description'}

## Valeur Business
${spec.specification?.businessValue || 'Non définie'}

## User Stories
${spec.specification?.userStories?.map((story, idx) => `${idx + 1}. ${story}`).join('\n') || 'Aucune user story définie'}

## Critères d'Acceptation
${spec.specification?.acceptanceCriteria?.map((criteria, idx) => `${idx + 1}. ${criteria}`).join('\n') || 'Aucun critère défini'}

## Tests
${spec.specification?.testCases?.map((test, idx) => `${idx + 1}. ${test}`).join('\n') || 'Aucun test défini'}

---
Généré le ${new Date(spec.createdAt).toLocaleDateString('fr-FR')}`;

        navigator.clipboard.writeText(specText).then(() => {
            toast.success(t('specificationsSection.specCopied'));
        }).catch(() => {
            toast.error('Erreur lors de la copie');
        });
    };

    const handleEditSpecification = (spec) => {
        const specData = spec.content?.specification || {};
        
        // Fonction utilitaire pour normaliser les arrays/strings
        const normalizeField = (field) => {
            if (!field) return [];
            if (Array.isArray(field)) return field;
            if (typeof field === 'string') return field.split('\n').filter(line => line.trim());
            return [];
        };

        const normalizeString = (field) => {
            if (!field) return '';
            if (Array.isArray(field)) return field.join('\n');
            return String(field);
        };

        setEditingSpec({
            id: spec.id,
            // Informations de base
            title: specData.title || '',
            summary: specData.summary || '',
            functionalDescription: specData.functionalDescription || '',
            businessValue: specData.businessValue || '',
            priority: spec.content?.priority || 'medium',
            
            // Advanced sections - with normalization
            personas: normalizeField(specData.personas),
            useCases: normalizeField(specData.useCases),
            userStories: normalizeField(specData.userStories),
            functionalRequirements: normalizeField(specData.functionalRequirements),
            nonFunctionalRequirements: normalizeField(specData.nonFunctionalRequirements),
            acceptanceCriteria: normalizeField(specData.acceptanceCriteria),
            testCases: normalizeField(specData.testCases || specData.mainTestCases),
            risks: normalizeField(specData.risks),
            dependencies: normalizeField(specData.dependencies),
            impacts: normalizeField(specData.impacts),
            definitionOfDone: normalizeField(specData.definitionOfDone),
            
            // Champs techniques
            architecture: normalizeString(specData.architecture || specData.technicalDetails || specData.systemArchitecture),
            wireframes: normalizeField(specData.wireframes || specData.mockups),
            dataModel: normalizeString(specData.dataModel || specData.entities || specData.databaseDesign),
            security: normalizeString(specData.security || (specData.securityRequirements && specData.securityRequirements.join('\n'))),
            performance: normalizeString(specData.performance || (specData.performanceRequirements && specData.performanceRequirements.join('\n'))),
            api: normalizeString(specData.api || specData.endpoints || specData.apiSpecification),
            constraints: normalizeString(specData.constraints || (specData.technicalConstraints && specData.technicalConstraints.join('\n'))),
            timeline: normalizeString(specData.timeline || specData.milestones || specData.deadlines),
            stakeholders: normalizeField(specData.stakeholders),
            glossary: normalizeString(specData.glossary || (specData.definitions && specData.definitions.join('\n'))),
            annexes: normalizeString(specData.annexes || specData.attachments || specData.references)
        });
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (!editingSpec || !editingSpec.title.trim()) {
            toast.error('Le titre est requis');
            return;
        }

        try {
            setLoading(true);
            
            // Prepare update data
            const updatedSpecification = {
                title: editingSpec.title.trim(),
                summary: editingSpec.summary.trim(),
                functionalDescription: editingSpec.functionalDescription.trim(),
                businessValue: editingSpec.businessValue.trim(),
                
                // Advanced sections - all data
                personas: editingSpec.personas,
                useCases: editingSpec.useCases,
                userStories: editingSpec.userStories,
                functionalRequirements: editingSpec.functionalRequirements,
                nonFunctionalRequirements: editingSpec.nonFunctionalRequirements,
                acceptanceCriteria: editingSpec.acceptanceCriteria,
                testCases: editingSpec.testCases,
                risks: editingSpec.risks,
                dependencies: editingSpec.dependencies,
                impacts: editingSpec.impacts,
                definitionOfDone: editingSpec.definitionOfDone,
                
                // Aspects techniques
                architecture: editingSpec.architecture,
                wireframes: editingSpec.wireframes,
                dataModel: editingSpec.dataModel,
                security: editingSpec.security,
                performance: editingSpec.performance,
                api: editingSpec.api,
                constraints: editingSpec.constraints,
                
                // Planning et organisation
                timeline: editingSpec.timeline,
                stakeholders: editingSpec.stakeholders,
                glossary: editingSpec.glossary,
                annexes: editingSpec.annexes
            };

            // 1. Update via API
            const response = await axios.put(
                `${API_BASE_URL}/api/specifications/${editingSpec.id}`, 
                {
                    specification: updatedSpecification,
                    priority: editingSpec.priority
                },
                { withCredentials: true }
            );

            if (response.data.success) {
                console.log('✅ Spécification mise à jour via API');
                
                // 2. Refresh the list
                refreshSpecifications();
                
                // 3. Fermer le modal
                setShowEditModal(false);
                setEditingSpec(null);
                
                toast.success(t('specificationsSection.specUpdated'));
            }

        } catch (error) {
            console.error('❌ Erreur lors de la mise à jour:', error);
            toast.error(t('specificationsSection.specUpdateError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setShowEditModal(false);
        setEditingSpec(null);
        setExpandedSections({ basics: true }); // Reset des sections
    };

    const toggleSection = (sectionName) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }));
    };

    // Fonction pour convertir les arrays en strings et vice versa
    const updateArrayField = (fieldName, value) => {
        const lines = value.split('\n').filter(line => line.trim());
        setEditingSpec(prev => ({
            ...prev,
            [fieldName]: lines
        }));
    };

    // === Unified structured rendering helpers (enhanced) ===
    const SECTION_RENDERERS = {
      business: (data) => {
        if (!data || typeof data !== 'object') return '';
        const objectives = data.objectives || data.goals || [];
        const kpis = data.kpis || data.metrics || [];
        return `<div class="structured-block business-block">
          ${objectives.length ? `<div class="sub"><h5>🎯 Objectives</h5><ul>${objectives.map(o=>`<li>${o}</li>`).join('')}</ul></div>`:''}
          ${kpis.length ? `<div class="sub"><h5>📊 KPIs</h5><ul>${kpis.map(k=>`<li>${k}</li>`).join('')}</ul></div>`:''}
          ${data.roi ? `<div class="sub"><h5>💹 ROI</h5><p>${data.roi}</p></div>`:''}
        </div>`;
      },
      stories: (items) => {
        return renderGenericItems(items, 'story', 'US', '👤');
      },
      tests: (items) => {
        return renderGenericItems(items, 'test', 'TC', '🧪');
      },
      acceptance: (items) => {
        return renderGenericItems(items, 'acceptance', 'AC', '✅');
      },
      risks: (items) => {
        return renderGenericItems(items, 'risk', 'R', '⚠️');
      },
      requirements: (items) => {
        return renderGenericItems(items, 'requirement', 'REQ', '📋');
      },
      // Fallback générique pour toutes les autres sections
      generic: (items) => {
        return renderGenericItems(items, 'item', 'ITEM', '📄');
      }
    };

    // Fonction utilitaire pour rendre des éléments de manière uniforme
    const renderGenericItems = (items, type, prefix, icon) => {
      if (!items) return '<div class="empty-section">Aucun élément défini</div>';
      
      // Si c'est une string simple
      if (typeof items === 'string') {
        if (items.trim() === '') return '<div class="empty-section">Aucun élément défini</div>';
        return `<div class="structured-card ${type}-card">
          <div class="card-head">
            <span class="badge id">${prefix}1</span>
            <span class="badge type">${icon}</span>
          </div>
          <div class="content">${items}</div>
        </div>`;
      }

      // Si c'est un array
      if (Array.isArray(items)) {
        if (items.length === 0) return '<div class="empty-section">Aucun élément défini</div>';
        
        return items.map((item, i) => {
          if (typeof item === 'string') {
            return `<div class="structured-card ${type}-card">
              <div class="card-head">
                <span class="badge id">${prefix}${i+1}</span>
                <span class="badge type">${icon}</span>
              </div>
              <div class="content">${item}</div>
            </div>`;
          } else if (typeof item === 'object' && item !== null) {
            // Objet structuré
            const title = item.title || item.name || item.description || `${prefix}${i+1}`;
            const desc = item.description || item.story || item.criteria || '';
            
            return `<div class="structured-card ${type}-card">
              <div class="card-head">
                <span class="badge id">${item.id || `${prefix}${i+1}`}</span>
                <span class="badge type">${icon}</span>
                ${item.priority ? `<span class="badge prio prio-${item.priority}">${item.priority}</span>` : ''}
                ${item.category ? `<span class="badge cat">${item.category}</span>` : ''}
              </div>
              <h4>${title}</h4>
              ${desc && desc !== title ? `<div class="content">${desc}</div>` : ''}
              ${renderObjectDetails(item)}
            </div>`;
          }
          return '';
        }).join('');
      }

      // Si c'est un objet unique
      if (typeof items === 'object' && items !== null) {
        const title = items.title || items.name || items.description || `${prefix}1`;
        const desc = items.description || items.story || items.criteria || '';
        
        return `<div class="structured-card ${type}-card">
          <div class="card-head">
            <span class="badge id">${items.id || `${prefix}1`}</span>
            <span class="badge type">${icon}</span>
            ${items.priority ? `<span class="badge prio prio-${items.priority}">${items.priority}</span>` : ''}
            ${items.category ? `<span class="badge cat">${items.category}</span>` : ''}
          </div>
          <h4>${title}</h4>
          ${desc && desc !== title ? `<div class="content">${desc}</div>` : ''}
          ${renderObjectDetails(items)}
        </div>`;
      }

      return '<div class="empty-section">Format non reconnu</div>';
    };

    // Fonction pour rendre les détails d'un objet
    const renderObjectDetails = (obj) => {
      let details = '';
      
      // Champs spéciaux selon le type
      if (obj.acceptanceCriteria && Array.isArray(obj.acceptanceCriteria)) {
        details += `<div class="mini-section"><strong>Critères d'acceptation:</strong><ul>${obj.acceptanceCriteria.map(c => `<li>${c}</li>`).join('')}</ul></div>`;
      }
      
      if (obj.steps && Array.isArray(obj.steps)) {
        details += `<div class="mini-section"><strong>Étapes:</strong><ol>${obj.steps.map(s => `<li>${s}</li>`).join('')}</ol></div>`;
      }
      
      if (obj.preconditions && Array.isArray(obj.preconditions)) {
        details += `<div class="mini-section"><strong>Préconditions:</strong><ul>${obj.preconditions.map(p => `<li>${p}</li>`).join('')}</ul></div>`;
      }
      
      if (obj.expectedResult) {
        details += `<div class="result"><strong>Résultat attendu:</strong> ${obj.expectedResult}</div>`;
      }
      
      if (obj.mitigation) {
        details += `<div class="mitigation"><strong>Mitigation:</strong> ${obj.mitigation}</div>`;
      }
      
      if (obj.probability || obj.impact) {
        details += `<div class="risk-meta">
          ${obj.probability ? `<span class="badge prob prob-${obj.probability}">P:${obj.probability}</span>` : ''}
          ${obj.impact ? `<span class="badge impact impact-${obj.impact}">I:${obj.impact}</span>` : ''}
        </div>`;
      }
      
      return details;
    };    return (
        <div id="section-specifications" className="section-specifications">
            <div className="section-specifications-header">
                <div className="section-specifications-icon">
                    <span className="section-specifications-icon-emoji">🎯</span>
                </div>
                <div>
                    <h3 className="section-specifications-title">
                        {t('specificationsSection.title')}
                    </h3>
                    <p className="section-specifications-desc">
                        {t('specificationsSection.description')}
                    </p>
                </div>
            </div>

            <div className="specifications-management">
                <div className="specifications-actions">
                    <div>
                        <button
                            onClick={handleOpenSpecificationGenerator}
                            className="open-spec-generator-btn"
                            disabled={loading}
                        >
                            <span className="btn-icon">🎯</span>
                            <span className="btn-text">{t('specificationsSection.newSpecification')}</span>
                        </button>

                        <button
                            onClick={refreshSpecifications}
                            className="refresh-specs-btn"
                            disabled={loading}
                            title={t('titles.refreshSpecifications')}
                        >
                            <span className="btn-icon">🔄</span>
                            <span className="btn-text">{loading ? t('common.loading') : t('common.refresh')}</span>
                        </button>
                    </div>

                    <div className="specifications-stats">
                        <span className="spec-count">
                            {t('specificationsSection.specsGenerated', { count: generatedSpecifications.length })}
                        </span>
                        {generatedSpecifications.length > 0 && (
                            <button
                                onClick={handleClearAllSpecifications}
                                className="clear-all-specs-btn"
                                title={t('titles.deleteAllSpecs')}
                            >
                                {t('specificationsSection.clearAll')}
                            </button>
                        )}
                    </div>
                </div>

                {generatedSpecifications.length === 0 ? (
                    <div className="specifications-empty">
                        <div className="empty-state-icon">📋</div>
                        <h4 className="empty-state-title">Aucune spécification générée</h4>
                        <p className="empty-state-desc">
                            Utilisez le générateur IA pour créer des spécifications fonctionnelles complètes
                        </p>
                        <button
                            onClick={handleOpenSpecificationGenerator}
                            className="empty-state-btn"
                            disabled={loading}
                        >
                            <span className="btn-icon">🎯</span>
                            Commencer
                        </button>
                    </div>
                ) : (
                    <div className="specifications-list">
                        {generatedSpecifications.map((spec, index) => (
                            <div key={spec.id || index} className="specification-card">
                                <div className="spec-card-header">
                                    <h4 className="spec-card-title">
                                        {spec.content?.specification?.title || spec.title || 'Spécification sans titre'}
                                    </h4>
                                    <div className="spec-card-meta">
                                        <span className="spec-date">
                                            {new Date(spec.createdAt).toLocaleDateString('fr-FR')}
                                        </span>
                                        {spec.content?.priority && (
                                            <span className="spec-priority">
                                                {spec.content.priority}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="spec-card-content">
                                    <p className="spec-summary">
                                        {spec.content?.specification?.summary || 
                                         spec.content?.specification?.functionalDescription || 
                                         spec.description || 
                                         'Aucune description disponible'}
                                    </p>

                                    <div className="spec-card-stats">
                                        {spec.content?.specification?.userStories && spec.content.specification.userStories.length > 0 && (
                                            <span className="spec-stat">
                                                👤 {spec.content.specification.userStories.length} User Stories
                                            </span>
                                        )}
                                        {spec.content?.specification?.acceptanceCriteria && spec.content.specification.acceptanceCriteria.length > 0 && (
                                            <span className="spec-stat">
                                                ✅ {spec.content.specification.acceptanceCriteria.length} Critères
                                            </span>
                                        )}
                                        {spec.content?.specification?.testCases && spec.content.specification.testCases.length > 0 && (
                                            <span className="spec-stat">
                                                🧪 {spec.content.specification.testCases.length} Tests
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="spec-card-actions">
                                    <button
                                        onClick={() => handleViewSpecification(spec)}
                                        className="spec-action-btn view"
                                    >
                                        👁️ {t('specificationsSection.actions.view')}
                                    </button>
                                    <button
                                        onClick={() => handleEditSpecification(spec)}
                                        className="spec-action-btn edit"
                                    >
                                        ✏️ {t('specificationsSection.actions.edit')}
                                    </button>
                                    <button
                                        onClick={() => handleExportSpecification(spec)}
                                        className="spec-action-btn export"
                                    >
                                        💾 {t('specificationsSection.actions.export')}
                                    </button>
                                    <button
                                        onClick={() => handleCopySpecification(spec)}
                                        className="spec-action-btn copy"
                                    >
                                        📋 {t('specificationsSection.actions.copy')}
                                    </button>
                                    {onCreateNodesFromSpecification && (
                                        <button
                                            onClick={() => onCreateNodesFromSpecification(spec)}
                                            className="spec-action-btn create-nodes"
                                            title="Créer automatiquement des nœuds Feature + Tests depuis cette spécification"
                                        >
                                            🔄 Générer des nœuds
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteSpecification(spec.id)}
                                        className="spec-action-btn delete"
                                    >
                                        🗑️ {t('specificationsSection.actions.delete')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal d'édition */}
            {showEditModal && editingSpec && (
                <div className="edit-modal-overlay" onClick={handleCancelEdit}>
                    <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="edit-modal-header">
                            <h3>✏️ {t('specificationsSection.editModal.title')}</h3>
                            <button 
                                className="close-modal-btn"
                                onClick={handleCancelEdit}
                            >
                                ❌
                            </button>
                        </div>
                        
                        <div className="edit-modal-content">
                            {/* Section Informations de base */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('basics')}>
                                    <h4>📋 {t('nodeForm.basicInfo')}</h4>
                                    <span className="toggle-icon">{expandedSections.basics ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.basics && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-title">{t('specificationsSection.editModal.labels.title')}</label>
                                            <input
                                                id="edit-title"
                                                type="text"
                                                value={editingSpec.title}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    title: e.target.value
                                                }))}
                                                placeholder={t('placeholders.specTitle')}
                                            />
                                        </div>

                                        <div className="edit-field">
                                            <label htmlFor="edit-summary">{t('specificationsSection.editModal.labels.summary')}</label>
                                            <textarea
                                                id="edit-summary"
                                                value={editingSpec.summary}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    summary: e.target.value
                                                }))}
                                                placeholder={t('placeholders.specSummary')}
                                                rows="3"
                                            />
                                        </div>

                                        <div className="edit-field">
                                            <label htmlFor="edit-description">{t('specificationsSection.editModal.labels.functionalDescription')}</label>
                                            <textarea
                                                id="edit-description"
                                                value={editingSpec.functionalDescription}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    functionalDescription: e.target.value
                                                }))}
                                                placeholder={t('placeholders.specDescription')}
                                                rows="4"
                                            />
                                        </div>

                                        <div className="edit-field">
                                            <label htmlFor="edit-business">{t('specificationsSection.editModal.labels.businessValue')}</label>
                                            <textarea
                                                id="edit-business"
                                                value={editingSpec.businessValue}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    businessValue: e.target.value
                                                }))}
                                                placeholder={t('placeholders.businessValue')}
                                                rows="3"
                                            />
                                        </div>

                                        <div className="edit-field">
                                            <label htmlFor="edit-priority">{t('specificationsSection.editModal.labels.priority')}</label>
                                            <select
                                                id="edit-priority"
                                                value={editingSpec.priority}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    priority: e.target.value
                                                }))}
                                            >
                                                <option value="low">{t('specificationsSection.editModal.priorities.low')}</option>
                                                <option value="medium">{t('specificationsSection.editModal.priorities.medium')}</option>
                                                <option value="high">{t('specificationsSection.editModal.priorities.high')}</option>
                                                <option value="critical">{t('specificationsSection.editModal.priorities.critical')}</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Personas */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('personas')}>
                                    <h4>👥 {t('specificationsSection.editModal.sections.personas')}</h4>
                                    <span className="toggle-icon">{expandedSections.personas ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.personas && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-personas">{t('specificationsSection.editModal.labels.personas')}</label>
                                            <textarea
                                                id="edit-personas"
                                                value={Array.isArray(editingSpec.personas) ? editingSpec.personas.join('\n') : editingSpec.personas}
                                                onChange={(e) => updateArrayField('personas', e.target.value)}
                                                placeholder={t('placeholders.userProfiles')}
                                                rows="4"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Use Cases */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('useCases')}>
                                    <h4>🎯 {t('specificationsSection.editModal.sections.useCases')}</h4>
                                    <span className="toggle-icon">{expandedSections.useCases ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.useCases && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-use-cases">{t('specificationsSection.editModal.labels.useCases')}</label>
                                            <textarea
                                                id="edit-use-cases"
                                                value={Array.isArray(editingSpec.useCases) ? editingSpec.useCases.join('\n') : editingSpec.useCases}
                                                onChange={(e) => updateArrayField('useCases', e.target.value)}
                                                placeholder={t('specificationsSection.placeholders.useCases')}
                                                rows="4"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section User Stories */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('userStories')}>
                                    <h4>📖 {t('specificationsSection.editModal.sections.userStories')}</h4>
                                    <span className="toggle-icon">{expandedSections.userStories ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.userStories && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-user-stories">{t('specificationsSection.editModal.labels.userStories')}</label>
                                            <textarea
                                                id="edit-user-stories"
                                                value={Array.isArray(editingSpec.userStories) ? editingSpec.userStories.join('\n') : editingSpec.userStories}
                                                onChange={(e) => updateArrayField('userStories', e.target.value)}
                                                placeholder={t('placeholders.userStories')}
                                                rows="5"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Critères d'acceptation */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('acceptance')}>
                                    <h4>✅ {t('specificationsSection.editModal.sections.acceptance')}</h4>
                                    <span className="toggle-icon">{expandedSections.acceptance ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.acceptance && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-acceptance">{t('specificationsSection.editModal.labels.acceptanceCriteria')}</label>
                                            <textarea
                                                id="edit-acceptance"
                                                value={Array.isArray(editingSpec.acceptanceCriteria) ? editingSpec.acceptanceCriteria.join('\n') : editingSpec.acceptanceCriteria}
                                                onChange={(e) => updateArrayField('acceptanceCriteria', e.target.value)}
                                                placeholder={t('specificationsSection.placeholders.functionalRequirements')}
                                                rows="5"
                                                                                       />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Tests */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('tests')}>
                                    <h4>🧪 {t('specificationsSection.editModal.sections.tests')}</h4>
                                    <span className="toggle-icon">{expandedSections.tests ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.tests && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-tests">{t('specificationsSection.editModal.labels.testCases')}</label>
                                            <textarea
                                                id="edit-tests"
                                                value={Array.isArray(editingSpec.testCases) ? editingSpec.testCases.join('\n') : editingSpec.testCases}
                                                onChange={(e) => updateArrayField('testCases', e.target.value)}
                                                placeholder={t('specificationsSection.editModal.placeholders.testCases')}
                                                rows="4"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Exigences */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('requirements')}>
                                    <h4>⚙️ {t('specificationsSection.editModal.sections.requirements')}</h4>
                                    <span className="toggle-icon">{expandedSections.requirements ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.requirements && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-functional-req">{t('specificationsSection.editModal.labels.functionalRequirements')}</label>
                                            <textarea
                                                id="edit-functional-req"
                                                value={Array.isArray(editingSpec.functionalRequirements) ? editingSpec.functionalRequirements.join('\n') : editingSpec.functionalRequirements}
                                                onChange={(e) => updateArrayField('functionalRequirements', e.target.value)}
                                                placeholder={t('specificationsSection.placeholders.functionalRequirements')}
                                                rows="4"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-nonfunctional-req">{t('specificationsSection.editModal.labels.nonFunctionalRequirements')}</label>
                                            <textarea
                                                id="edit-nonfunctional-req"
                                                value={Array.isArray(editingSpec.nonFunctionalRequirements) ? editingSpec.nonFunctionalRequirements.join('\n') : editingSpec.nonFunctionalRequirements}
                                                onChange={(e) => updateArrayField('nonFunctionalRequirements', e.target.value)}
                                                placeholder={t('specificationsSection.placeholders.nonFunctionalRequirements')}
                                                rows="3"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Risques et Dépendances */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('risks')}>
                                    <h4>⚠️ {t('specificationsSection.editModal.sections.risks')}</h4>
                                    <span className="toggle-icon">{expandedSections.risks ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.risks && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-risks">{t('specificationsSection.editModal.labels.risks')}</label>
                                            <textarea
                                                id="edit-risks"
                                                value={Array.isArray(editingSpec.risks) ? editingSpec.risks.join('\n') : editingSpec.risks}
                                                onChange={(e) => updateArrayField('risks', e.target.value)}
                                                placeholder={t('specificationsSection.editModal.placeholders.risks')}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-dependencies">{t('specificationsSection.editModal.labels.dependencies')}</label>
                                            <textarea
                                                id="edit-dependencies"
                                                value={Array.isArray(editingSpec.dependencies) ? editingSpec.dependencies.join('\n') : editingSpec.dependencies}
                                                onChange={(e) => updateArrayField('dependencies', e.target.value)}
                                                placeholder={t('specificationsSection.editModal.placeholders.dependencies')}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-impacts">{t('specificationsSection.editModal.labels.impacts')}</label>
                                            <textarea
                                                id="edit-impacts"
                                                value={Array.isArray(editingSpec.impacts) ? editingSpec.impacts.join('\n') : editingSpec.impacts}
                                                onChange={(e) => updateArrayField('impacts', e.target.value)}
                                                placeholder={t('specificationsSection.editModal.placeholders.impacts')}
                                                rows="3"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Technique */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('technical')}>
                                    <h4>🏗️ {t('specificationsSection.editModal.sections.technical')}</h4>
                                    <span className="toggle-icon">{expandedSections.technical ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.technical && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-architecture">{t('specificationsSection.editModal.labels.architecture')}</label>
                                            <textarea
                                                id="edit-architecture"
                                                value={editingSpec.architecture}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    architecture: e.target.value
                                                }))}
                                                placeholder={t('specificationsSection.editModal.placeholders.architecture')}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-data-model">{t('specificationsSection.editModal.labels.dataModel')}</label>
                                            <textarea
                                                id="edit-data-model"
                                                value={editingSpec.dataModel}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    dataModel: e.target.value
                                                }))}
                                                placeholder={t('specificationsSection.editModal.placeholders.dataModel')}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-api">{t('specificationsSection.editModal.labels.api')}</label>
                                            <textarea
                                                id="edit-api"
                                                value={editingSpec.api}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    api: e.target.value
                                                }))}
                                                placeholder={t('specificationsSection.editModal.placeholders.api')}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-security">{t('specificationsSection.editModal.labels.security')}</label>
                                            <textarea
                                                id="edit-security"
                                                value={editingSpec.security}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    security: e.target.value
                                                }))}
                                                placeholder={t('specificationsSection.editModal.placeholders.security')}
                                                rows="2"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-performance">{t('specificationsSection.editModal.labels.performance')}</label>
                                            <textarea
                                                id="edit-performance"
                                                value={editingSpec.performance}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    performance: e.target.value
                                                }))}
                                                placeholder="Exigences de performance"
                                                rows="2"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Section Planning et Parties Prenantes */}
                            <div className="edit-section">
                                <div className="edit-section-header" onClick={() => toggleSection('planning')}>
                                    <h4>📅 {t('specificationsSection.editModal.sections.planning')}</h4>
                                    <span className="toggle-icon">{expandedSections.planning ? '▼' : '▶'}</span>
                                </div>
                                {expandedSections.planning && (
                                    <div className="edit-section-content">
                                        <div className="edit-field">
                                            <label htmlFor="edit-timeline">Planning</label>
                                            <textarea
                                                id="edit-timeline"
                                                value={editingSpec.timeline}
                                                onChange={(e) => setEditingSpec(prev => ({
                                                    ...prev,
                                                    timeline: e.target.value
                                                }))}
                                                placeholder="Échéances, milestones..."
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-stakeholders">Parties Prenantes (une par ligne)</label>
                                            <textarea
                                                id="edit-stakeholders"
                                                value={Array.isArray(editingSpec.stakeholders) ? editingSpec.stakeholders.join('\n') : editingSpec.stakeholders}
                                                onChange={(e) => updateArrayField('stakeholders', e.target.value)}
                                                placeholder="Équipes, personnes impliquées..."
                                                rows="3"
                                            />
                                        </div>
                                        <div className="edit-field">
                                            <label htmlFor="edit-dod">Definition of Done (une par ligne)</label>
                                            <textarea
                                                id="edit-dod"
                                                value={Array.isArray(editingSpec.definitionOfDone) ? editingSpec.definitionOfDone.join('\n') : editingSpec.definitionOfDone}
                                                onChange={(e) => updateArrayField('definitionOfDone', e.target.value)}
                                                placeholder="Critères de fin de développement..."
                                                rows="3"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="edit-modal-actions">
                            <button 
                                className="cancel-edit-btn"
                                onClick={handleCancelEdit}
                                disabled={loading}
                            >
                                {t('specificationsSection.editModal.buttons.cancel')}
                            </button>
                            <button 
                                className="save-edit-btn"
                                onClick={handleSaveEdit}
                                disabled={loading || !editingSpec.title.trim()}
                            >
                                {loading ? `💾 ${t('specificationsSection.editModal.buttons.saving')}` : `💾 ${t('specificationsSection.editModal.buttons.save')}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecificationsSection;
